import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { TESTIDS } from "@/constants/testIds";
import { Hammer, Lock } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1625008668243-e10fa6121030?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxzdG9uZSUyMG1hc29ucnklMjB0ZXh0dXJlfGVufDB8fHx8MTc4MTEzODQ3MXww&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const { login, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      const dest = location.state?.from?.pathname || "/";
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left: form */}
      <div className="flex flex-col px-6 sm:px-12 lg:px-20 py-8 sm:py-10">
        <div className="flex items-center gap-3 mb-10 sm:mb-16">
          <img src="/edenstone-logo.png" alt="Edenstone Masonry" className="w-16 h-16 sm:w-20 sm:h-20 object-contain bg-white rounded-md p-1.5 ring-1 ring-zinc-200" />
          <div>
            <div className="font-display font-extrabold text-zinc-900 text-base leading-none tracking-tight">EDENSTONE</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-1">Masonry Ops</div>
          </div>
        </div>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-sm">
            <div className="es-overline mb-3 text-brand-600">// access terminal</div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-zinc-900 mb-3 leading-tight">
              Sign in to the<br />operations console.
            </h1>
            <p className="text-sm text-zinc-600 mb-10">
              Internal staff, warehouse and partner access. All sessions are logged.
            </p>

            <form onSubmit={onSubmit} className="space-y-5">
              <div>
                <label className="es-label" htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  required
                  autoFocus
                  data-testid={TESTIDS.loginEmail}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="es-input"
                  placeholder="you@edenstone.com"
                />
              </div>
              <div>
                <label className="es-label" htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  required
                  data-testid={TESTIDS.loginPassword}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="es-input"
                  placeholder="••••••••"
                />
              </div>

              {error && (
                <div data-testid={TESTIDS.loginError} className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 font-medium rounded-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid={TESTIDS.loginSubmit}
                className="es-btn-primary w-full h-11"
              >
                {loading ? "Authorising…" : (<><Lock className="w-4 h-4" /> Sign in</>)}
              </button>

              <div className="text-center pt-1">
                <button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  className="text-xs font-semibold text-zinc-500 hover:text-brand-700"
                  data-testid="login-forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="mt-10 flex items-center gap-2 text-[11px] text-zinc-400 font-mono uppercase tracking-widest">
          <Hammer className="w-3 h-3" /> Edenstone Masonry
        </div>
      </div>

      {/* Right: hero image */}
      <div className="hidden lg:block relative bg-zinc-900">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900/85 via-zinc-900/40 to-brand-900/40" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <div className="es-overline mb-4 text-brand-400">// since 2002</div>
          <h2 className="font-display text-4xl font-black leading-tight tracking-tight mb-4">
            Australian-made.<br />
            Hand-crafted.<br />
            <span className="text-brand-500">Built to endure.</span>
          </h2>
          <p className="text-sm text-zinc-300 max-w-md">
            One platform for inventory, dispatch, reseller pricing and the entire sales pipeline — built for the yard and the office.
          </p>
        </div>
      </div>
    </div>
  );
}
